import {
  DrawerBody,
  DrawerCloseButton,
  DrawerContent,
  DrawerContentProps,
  DrawerFooter,
  DrawerHeader,
  Flex,
  FormControl,
  FormLabel,
  IconButton,
  Stack,
  Table,
  TableContainer,
  Tbody,
  Td,
  Th,
  Thead,
  Tr,
} from '@chakra-ui/react';
import { Omit } from '@reduxjs/toolkit/dist/tsHelpers';
import { useFetchAccountsQuery } from 'api';
import { useAuth } from 'core';
import Decimal from 'decimal.js';
import { AppInput, CurrencyLabel, FormButtonGroup } from 'features/components';
import { every } from 'lodash';
import { useMemo, useState } from 'react';
import {
  Controller,
  FormProvider,
  useFieldArray,
  useForm,
} from 'react-hook-form';
import { FiEdit, FiPlus } from 'react-icons/fi';
import {
  WorkOrderRevisionAccountDrawer,
  WorkOrderRevisionAccountDrawerProps,
} from '.';
import { PostWorkOrderTransactionAccountRequest } from 'api/bindings/PostWorkOrderTransactionAccountRequest';
import { PostWorkOrderTransactionRequest } from 'api/bindings/PostWorkOrderTransactionRequest';
import { PostWorkOrderRequest } from 'api/bindings/PostWorkOrderRequest';
import { PutWorkOrderRequest } from 'api/bindings/PutWorkOrderRequest';

type WorkOrderModel = PostWorkOrderRequest | PutWorkOrderRequest;
type Row = PostWorkOrderTransactionAccountRequest;

type Form = PostWorkOrderTransactionRequest;

export interface WorkOrderFormRevisionDrawerContentProps
  extends Omit<DrawerContentProps, 'onSubmit'> {
  workOrderName: string;
  afeId: string;
  onClose: () => void;
  onSubmit: (form: Form) => void;
}

export const WorkOrderFormRevisionDrawerContent = (
  props: WorkOrderFormRevisionDrawerContentProps
) => {
  const { workOrderName, afeId, onClose, onSubmit, ...rest } = props;

  // Fetches
  const auth = useAuth();
  const { data: accountsResponse } = useFetchAccountsQuery(
    { clientId: auth?.user.clientId ?? '0' },
    { skip: !auth?.user.clientId }
  );

  // State
  let allSubAccounts = useMemo(
    () => accountsResponse?.items.flatMap((x) => x.subAccounts),
    [accountsResponse?.items]
  );

  const [accountDrawerSettings, setAccountDrawerSettings] = useState<
    Omit<WorkOrderRevisionAccountDrawerProps, 'onSubmit' | 'onClose'> & {
      editingIndex: number;
    }
  >();

  const form = useForm<Form>({
    mode: 'all',
    defaultValues: {
      accounts: [],
    },
  });
  const { formState, control, reset, watch, handleSubmit } = form;

  const accountsFieldArray = useFieldArray({
    name: 'accounts',
    control,
    keyName: '_id',
    rules: {
      validate: {
        min: (v) => v.length > 0,
        allSubAccounts: (v) => every(v, (x) => BigInt(x.subAccountId) > 0),
        allAmountsPositive: (v) => every(v, (x) => new Decimal(x.amount).gt(0)),
      },
    },
  });

  const handleRowSubmit = (row: Row) => {
    if (!accountDrawerSettings) return;
    const { editingIndex: ediingIndex } = accountDrawerSettings;

    if (ediingIndex === -1) {
      accountsFieldArray.append(row);
    } else {
      accountsFieldArray.remove(ediingIndex);
      accountsFieldArray.insert(ediingIndex, row);
    }
    // setEditingAccount({ ...editingAccount, isEditModalOpen: false });
    setAccountDrawerSettings(undefined);
  };

  const nameField = watch('name');

  const handleClose = () => {
    reset();
    onClose();
  };
  return (
    <FormProvider {...form}>
      <DrawerContent {...rest}>
        <form onSubmit={handleSubmit(onSubmit)}>
          <DrawerCloseButton />
          <DrawerHeader>
            New Work Order Revision for {workOrderName}: {nameField}
          </DrawerHeader>
          <DrawerBody>
            {accountDrawerSettings && (
              <WorkOrderRevisionAccountDrawer
                {...accountDrawerSettings}
                onClose={() => setAccountDrawerSettings(undefined)}
                onSubmit={handleRowSubmit}
              />
            )}
            <Stack
              direction={'column'}
              spacing="5"
              px={{ base: 4, md: 6 }}
              py={{ base: 5, md: 6 }}
            >
              <FormControl id="name">
                <FormLabel>Name:</FormLabel>
                <Controller
                  name="name"
                  control={control}
                  rules={{
                    required: 'Name is required',
                    minLength: {
                      value: 3,
                      message: 'Name must be between 3 and 256 characters',
                    },
                    maxLength: {
                      value: 256,
                      message: 'Name must be between 3 and 256 characters',
                    },
                  }}
                  render={(props) => <AppInput {...props.field} />}
                />
              </FormControl>
              <FormControl id="accounts">
                <Flex>
                  <FormLabel flexGrow={1}>Accounts:</FormLabel>
                  <IconButton
                    icon={
                      // @ts-ignore
                      <FiPlus />
                    }
                    aria-label="New Account"
                    variant={'solid'}
                    colorScheme="blue"
                    onClick={() =>
                      setAccountDrawerSettings({
                        ...accountDrawerSettings,
                        afeId: afeId,
                        isOpen: true,
                        editingIndex: -1,
                        row: {
                          subAccountId: '0',
                          amount: '0',
                        },
                      })
                    }
                  />
                </Flex>
                <TableContainer>
                  <Table variant="simple">
                    <Thead>
                      <Tr>
                        <Th></Th>
                        <Th>Account</Th>
                        <Th>Amount</Th>
                      </Tr>
                    </Thead>
                    <Tbody>
                      {accountsFieldArray.fields.map((r, i) => (
                        <Tr key={r._id}>
                          <Td>
                            <IconButton
                              aria-label="Edit Row"
                              icon={
                                // @ts-ignore
                                <FiEdit />
                              }
                              onClick={() =>
                                setAccountDrawerSettings({
                                  ...accountDrawerSettings,
                                  afeId: afeId,
                                  isOpen: true,
                                  editingIndex: i,
                                  row: r,
                                })
                              }
                            />
                          </Td>
                          <Td>
                            {
                              allSubAccounts?.find(
                                (x) => x.subAccountId === r.subAccountId
                              )?.fullSubAccountName
                            }
                          </Td>
                          <Td>
                            <CurrencyLabel value={r.amount} />
                          </Td>
                        </Tr>
                      ))}
                    </Tbody>
                  </Table>
                </TableContainer>
              </FormControl>
            </Stack>
          </DrawerBody>
          <DrawerFooter>
            <FormButtonGroup
              onCancel={handleClose}
              onConfirm={handleSubmit(onSubmit)}
            />
          </DrawerFooter>
        </form>
      </DrawerContent>
    </FormProvider>
  );
};
